import React, { useState } from 'react';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { useMutation, useQuery } from '@apollo/react-hooks';

import { Typography } from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';

import { ACTIVITIES_WITH_BOOKINGS } from 'graphql/queries/activities';
import { CANCEL_ACTIVITY_BOOKING } from 'graphql/mutations/booking';

import ConfirmContext from 'contexts/ConfirmContext';

import {
  AppHeader,
  CircularLoader,
  UpcomingActivitiesDialog
} from 'components';

import CalendarGeneralRow from './components/calendar-general-row/CalendarGeneralRow';
import OverviewCalendarHeader from './components/overview-calendar-header/OverviewCalendarHeader';

import {
  getCalendarActivitiesBookings,
  guestsSumSeats
} from 'utils/bookings/prepareBookingsData';

import { getRangeList } from 'utils/datesMethods';

import useStyles from './styles';

const ACTIVITIES_TAB_INDEX = 1;

const isActivityAvailable = (availableDates, rangeItem, availableDays) => {
  const startDate = availableDates.startDate;
  const endDate = availableDates.endDate;
  const rangeDate = rangeItem.date;
  const rangeDay = moment(rangeItem.day, 'dddd').format('ddd');

  if (startDate && endDate) {
    return (
      moment(rangeDate).isBetween(startDate, endDate, 'day', '[]') &&
      availableDays[rangeDay]
    );
  }

  if (startDate) {
    return (
      moment(rangeDate).isSameOrAfter(startDate, 'day') &&
      availableDays[rangeDay]
    );
  }

  if (endDate) {
    return (
      moment(rangeDate).isSameOrBefore(endDate, 'day') &&
      availableDays[rangeDay]
    );
  }
};

const BookingsList = ({ activitiesBookings, rangeList, refetch }) => {
  const classes = useStyles();

  if (!activitiesBookings || !activitiesBookings.length) return null;

  return (
    <div className={classes.bookingsList}>
      {activitiesBookings?.map(booking => (
        <div key={booking.id}>
          <div
            className={classNames(
              classes.bookingRow,
              classes.bookingTitleWrapper
            )}
            >
            <Tooltip title={`${booking.productCode} ⋅ ${booking.title}`} arrow>
              <Typography variant="body1" className={classes.bookingTitle}>
                {booking.productCode} ⋅ {booking.title}
              </Typography>
            </Tooltip>
          </div>

          <TimesList
            booking={booking}
            activities={booking.activities}
            rangeList={rangeList}
            refetch={refetch}
          />
        </div>
      ))}
    </div>
  );
};

const BookingDay = ({ activity, isAvailable, booking, refetch, item }) => {
  const classes = useStyles();
  const [t] = useTranslation();
  const confirm = React.useContext(ConfirmContext);
  const { enqueueSnackbar } = useSnackbar();

  const [cancelActivityBookings] = useMutation(CANCEL_ACTIVITY_BOOKING);

  const [open, setOpen] = React.useState(false);
  const [dialogContent, setDialogContent] = React.useState({});

  const activityCancellationHandler = item => {
    const activityBookings = item.participants
    ?.map(booking => {
      return parseInt(booking.id);
    });
       
    confirm({
      title: `${t('Cancel all Activities')}?`,
      description: `Activity: ${item.title} • ${item.productCode}`,
      confirmationText: t('Cancel all Activities'),
      cancellationText: t('Back')
    }).then(() =>
      cancelActivityBookings({
        variables: { ids: activityBookings }
      }).then(() => {
        setOpen(false);
        enqueueSnackbar(t('Activity was successfully canceled!'), {
          variant: 'success'
        });
        refetch();
      })
    );
  };

  if (activity) {
    return (
      <>
        <Typography
          variant="body1"
          onClick={() => {
            setDialogContent(activity);
            setOpen(true);
          }}
          className={classNames(
            classes.activityparticipants,
            classes.participantsHover
          )}
        >
          <span>
            {guestsSumSeats(activity.guests)}/{activity.maxParticipants}
          </span>
          {activity.isPrivate && <span>P</span>}
        </Typography>

        <UpcomingActivitiesDialog
          activity={dialogContent}
          onClose={isRefetch => {
            setOpen(false);
            if (typeof isRefetch === 'boolean' && isRefetch) refetch();
          }}
          open={open}
          onCancel={activityCancellationHandler}
        />
      </>
    );
  }


  if (isAvailable) {

    let guestNumber = 0;
    let activityForThisDate = null;
    
    const hasBookingForThisDate = !!(
      booking?.activities?.length &&
      item?.date &&
      booking.activities.some(act => {
        const startingDate = act?.startingDate
          ? moment(act.startingDate, 'YYYY-MM-DD')
          : null;
        return startingDate?.isValid() && startingDate.isSame(item?.date, 'day');
      })
    );
    
      

    if (!hasBookingForThisDate) {
      guestNumber = 0; 
    } else {
      activityForThisDate = booking?.activities?.find(act => {
        if (!act || !act.startingDate || !item?.date) return false;
        const startingDate = moment(act.startingDate, 'YYYY-MM-DD');
        return startingDate && moment(startingDate).isSame(item?.date, 'day');
      });
      
    if (activityForThisDate?.guests?.[0]?.guestsNumber !== undefined) {
      guestNumber = activityForThisDate.guests[0].guestsNumber;
    }
  }
    
    return (
      <>
        <Typography
          variant="body1"
          onClick={() => {
            if (activityForThisDate) {
              setDialogContent(activityForThisDate);
              setOpen(true);
            }
          }}
          className={classNames(
            classes.activityparticipants,
            activityForThisDate ? classes.participantsHover : ''
          )}
        >
          <span>{guestNumber}/{booking.maxParticipants}</span>
          {booking.isPrivate && <span>P</span>}
        </Typography>

        {activityForThisDate && (
          <UpcomingActivitiesDialog
            activity={dialogContent}
            onClose={isRefetch => {
              setOpen(false);
              if (typeof isRefetch === 'boolean' && isRefetch) refetch();
            }}
            open={open}
            onCancel={activityCancellationHandler}
          />
        )}
      </>
    );
  }

  return (
    <Typography variant="body1" className={classes.activitynoParticipants}>
      {' '}
    </Typography>
  );
};

const DateNoTimeRow = ({
  rangeList,
  activities,
  schedule,
  days,
  booking,
  refetch
}) => {
  const classes = useStyles();

  return (
    <div
      className={classNames(classes.bookingRow, classes.bookingParticipantsRow)}
    >
      <Typography variant="body1" className={classes.bookingTime}>
        (Date no time)
      </Typography>
      {rangeList?.map((item, index) => {
        const rangeActivities =
          activities &&
          activities.filter(activity => {
            const startingDate = moment(activity.startingDate, 'YYYY-MM-DD');

            if (activity.endingDate) {
              const endingDate = moment(activity.endingDate, 'YYYY-MM-DD');
              if (item.date.isBetween(startingDate, endingDate, 'days', '[]')) {
                return activity;
              }
              return null;
            }

            const isSameDates = moment(startingDate).isSame(item.date, 'day');
            if (isSameDates) return activity;
            return null;
          });

        const activity =
          !!rangeActivities && rangeActivities.length
            ? { ...rangeActivities[0] }
            : null;

        if (activity) {
          activity.guests = [];
          activity.participants = [];
          rangeActivities.forEach(rangeActivity => {
            activity.guests.push(...rangeActivity.guests);
            activity.participants.push(rangeActivity);
          });
        }

        const isAvailable =
          !activity && isActivityAvailable(schedule.availability, item, days);

        return (
          <div
            key={index}
            className={classNames(classes.column, classes.participantsColumn)}
          >
            <BookingDay
              activity={activity}
              isAvailable={isAvailable}
              booking={booking}
              refetch={refetch}
            />
          </div>
        );
      })}
    </div>
  );
};

const TimesList = ({ booking, rangeList, refetch }) => {
  const classes = useStyles();
  const activities = booking?.activities;

  const schedule = booking?.schedule;

  if (!!schedule.days) {
    return (
      <DateNoTimeRow
        rangeList={rangeList}
        activities={activities}
        schedule={schedule}
        days={schedule.days}
        booking={booking}
        refetch={refetch}
      />
    );
  }
  return (
    <>
      {schedule?.daysArray?.map((day, index) => {
        const startTimeAndDuration = day.startTimeAndDuration || [];
        if (!startTimeAndDuration.length) {
          return (
            <div key={index}>
              <DateNoTimeRow
                rangeList={rangeList}
                activities={activities}
                schedule={schedule}
                days={day.days}
                booking={booking}
                refetch={refetch}
              />
            </div>
          );
        }
        return (
          <div key={index}>
            {startTimeAndDuration?.map((time, index) => {
              const timeId = `${moment(time.startTime).format('HH:mm')}-${
                time.duration
              }-${time.timeType}`;
              return (
                <div
                  key={index}
                  className={classNames(
                    classes.bookingRow,
                    classes.bookingParticipantsRow
                  )}
                >
                  <Typography variant="body1" className={classes.bookingTime}>
                    {moment(time.startTime).format('HH:mm')} ⋅ {time.duration}{' '}
                    {time.timeType}
                  </Typography>
                  {rangeList?.map((item, index) => {
                    const activity =
                      activities &&
                      activities?.find(activity => {
                      if (!activity?.startingDate || !item?.date) return false;
                        const startingDate = moment(
                          activity.startingDate,
                          'YYYY-MM-DD'
                        );
                        const isSameDate = moment(startingDate).isSame(
                          item.date,
                          'day'
                        );
                        if (isSameDate && activity.schedule?.id === timeId) {
                          return activity;
                        }
                        return false;
                      });
                    const isAvailable =
                      !activity &&
                      isActivityAvailable(
                        schedule?.availability,
                        item,
                        day?.days
                      );
                     
                      
                    return (
                      <div
                        key={index}
                        className={classNames(
                          classes.column,
                          classes.participantsColumn
                        )}
                      >
                        <BookingDay
                          booking={booking}
                          activity={activity}
                          isAvailable={isAvailable}
                          refetch={refetch}
                          item={item} 
                        />
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}
    </>
  );
};
const ActivitiesOverviewCalendar = ({ businessOwner }) => {
  const classes = useStyles();

  const [rangeDates, setRangeDates] = React.useState({
    startDate: moment().startOf('isoWeek'),
    endDate: moment().endOf('isoWeek')
  });
  const [activitiesBookings, setActivitiesBookings] = React.useState(null);
  const [rangeList, setRangeList] = React.useState(
    getRangeList(rangeDates.startDate, rangeDates.endDate)
  );
  const [selectedDate, setSelectedDate] = useState(null);

  const { loading, data, refetch } = useQuery(ACTIVITIES_WITH_BOOKINGS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      startPeriod: moment(rangeDates.startDate).format('YYYY-MM-DD'),
      endPeriod: moment(rangeDates.endDate).format('YYYY-MM-DD'),
      businessOwnerId:
        businessOwner.selectedBusinessOwner.ownerId ||
        businessOwner.selectedBusinessOwner.id
    }
  });

  React.useEffect(() => {
    if (!data) return;

    setRangeList(getRangeList(rangeDates.startDate, rangeDates.endDate));
    setActivitiesBookings(getCalendarActivitiesBookings(data.activities));
  }, [data]);

  const changeWeek = range => {
    setRangeDates(range);
  };

  return (
    <div className={classes.wrapper}>
      <AppHeader />

      <CircularLoader
        isLoading={loading}
        loaderClassName={classes.loaderClassName}
        className={classes.loaderWrapper}
      />

      <OverviewCalendarHeader
        startDate={rangeDates.startDate}
        endDate={rangeDates.endDate}
        changeWeekCallback={changeWeek}
        tabIndex={ACTIVITIES_TAB_INDEX}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
      />

      <div className={classes.calendarWrapper}>
        <CalendarGeneralRow
          rangeList={rangeList}
          title="Activities"
          selectedDate={selectedDate}
        />

       
            <BookingsList
              activitiesBookings={activitiesBookings}
              rangeList={rangeList}
              refetch={refetch}
            />
        
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  businessOwner: state.businessOwner
});

export default connect(mapStateToProps, null)(ActivitiesOverviewCalendar);